import { Box, Flex, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconBuildingSkyscraper,
  IconChartBar,
  IconGlobe,
  IconUsers,
  type TablerIcon,
} from "@tabler/icons-react";
import type { SectorEcosystemOverview } from "../../../../../../shared/sectorEcosystemOverview";
import type { SectorVisualConfig } from "../sectorEcosystem.const";
import { getStaticSectorCompanyStats, getStaticSectorCountryLabel, getStaticSectorSuccessStoriesLabel } from "../sectorEcosystem.const";

type MetricDef = {
  label: string;
  value?: number;
  staticValue?: string;
  comingSoon?: boolean;
  icon: TablerIcon;
  color: string;
  bg: string;
};

function formatCount(value: number) {
  if (value <= 0) return "0";
  return `${value}+`;
}

type SectorEcosystemHeroProps = {
  slug: string;
  overview: SectorEcosystemOverview;
  visual: SectorVisualConfig;
};

export default function SectorEcosystemHero({ slug, overview, visual }: SectorEcosystemHeroProps) {
  const SectorIcon = visual.icon;
  const companyStats = getStaticSectorCompanyStats(slug);
  const metrics: MetricDef[] = [
    {
      label: "Companies",
      staticValue: companyStats.totalLabel,
      icon: IconBuildingSkyscraper,
      color: "#2563EB",
      bg: "#EFF6FF",
    },
    {
      label: "Ecosystem Heads",
      comingSoon: true,
      icon: IconUsers,
      color: "#7C3AED",
      bg: "#F5F3FF",
    },
    {
      label: "Countries",
      staticValue: getStaticSectorCountryLabel(slug),
      icon: IconGlobe,
      color: "#16A34A",
      bg: "#F0FDF4",
    },
    {
      label: "Success Stories",
      staticValue: getStaticSectorSuccessStoriesLabel(slug),
      icon: IconChartBar,
      color: "#F97316",
      bg: "#FFEDD5",
    },
  ];

  return (
    <Paper
      radius="lg"
      p={{ base: "md", sm: "xl" }}
      style={{
        border: "1px solid #E2E8F0",
        background: "linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 55%, #EFF6FF 100%)",
        overflow: "hidden",
      }}
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="xl"
      >
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="sm" align="center">
            <ThemeIcon size={40} radius="md" style={{ backgroundColor: visual.iconBg, color: visual.iconColor }}>
              <SectorIcon size={22} />
            </ThemeIcon>
            <Title order={2} fz={{ base: 24, sm: 32 }} fw={700} c="#0F172A" lh={1.15} mt={0}>
              {overview.title} Ecosystem
            </Title>
            <Text
              size="xs"
              fw={600}
              px={10}
              py={4}
              style={{ borderRadius: 999, backgroundColor: "#DCFCE7", color: "#166534" }}
            >
              Active
            </Text>
          </Group>
          <Text c="dimmed" maw={560}>
            {visual.tagline}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <Paper key={metric.label} p="sm" radius="md" withBorder style={{ borderColor: "#E2E8F0" }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size={32} radius="md" style={{ backgroundColor: metric.bg, color: metric.color }}>
                      <Icon size={16} />
                    </ThemeIcon>
                    <Box>
                      {metric.comingSoon ? (
                        <Text fw={700} fz="sm" c="#64748B" lh={1.2}>
                          Coming Soon
                        </Text>
                      ) : metric.staticValue ? (
                        <Text fw={700} fz="lg" lh={1.1}>
                          {metric.staticValue}
                        </Text>
                      ) : (
                        <Text fw={700} fz="lg" lh={1.1}>
                          {formatCount(metric.value ?? 0)}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed">
                        {metric.label}
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
        <Box
          visibleFrom="md"
          w={280}
          h={180}
          style={{
            flexShrink: 0,
            borderRadius: 12,
            background: `linear-gradient(160deg, ${visual.iconBg} 0%, #fff 45%, #F1F5F9 100%)`,
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SectorIcon size={96} color={visual.iconColor} style={{ opacity: 0.35 }} />
        </Box>
      </Flex>
    </Paper>
  );
}
